/**
 * G2eGente - Página de Instalação do App PWA
 * Esta página é otimizada para conversão de instalação do PWA
 * Com instruções especiais detalhadas para iPhone/iOS
 */
import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { usePWA } from '../lib/usePWA';
import { toast } from 'sonner';
import { 
  Download, 
  Smartphone, 
  Check, 
  Shield,
  Bell,
  Zap,
  Briefcase,
  ChevronDown,
  Share,
  Plus,
  ExternalLink,
  AlertCircle,
  CheckCircle2
} from 'lucide-react';

export default function AppInstallPage() {
  const { canBeInstalled, isInstalled, install } = usePWA();
  const [showInstructions, setShowInstructions] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isAndroid, setIsAndroid] = useState(false);
  const [isSafari, setIsSafari] = useState(false);
  const [justInstalled, setJustInstalled] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);

  useEffect(() => {
    // Detect platform
    const ua = navigator.userAgent || navigator.vendor;
    const detectedIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    const detectedAndroid = /android/i.test(ua);
    
    // Detect Safari browser
    const detectedSafari = /^((?!chrome|android).)*safari/i.test(ua);
    
    setIsIOS(detectedIOS);
    setIsAndroid(detectedAndroid);
    setIsSafari(detectedSafari);
    
    // Show instructions by default on iOS since it doesn't support native PWA install prompt
    if (detectedIOS) {
      setShowInstructions(true);
    }
  }, []);

  const handleInstallClick = async () => {
    if (isInstalled || justInstalled) {
      toast.success('App instalado! Abra pelo ícone na tela inicial do seu celular.');
      return;
    }
    
    if (canBeInstalled) {
      const success = await install();
      if (success) {
        setJustInstalled(true);
        toast.success('App instalado! Agora abra pelo ícone na tela inicial.');
      } else {
        setShowInstructions(true);
      }
    } else {
      setShowInstructions(true);
    }
  };

  const features = [
    { icon: Zap, title: 'Acesso Rápido', desc: 'Abra direto da sua tela inicial' },
    { icon: Bell, title: 'Notificações', desc: 'Receba alertas de novas vagas' },
    { icon: Shield, title: 'Seguro', desc: 'Seus dados protegidos' },
    { icon: Briefcase, title: 'Vagas', desc: 'Encontre oportunidades' }
  ];

  const showInstallSuccess = isInstalled || justInstalled;

  // iPhone steps with detailed instructions
  const iphoneSteps = [
    {
      number: 1,
      title: 'Abra no Safari',
      description: 'O app só pode ser instalado pelo Safari. Se você está em outro navegador, copie o link e abra no Safari.',
      icon: ExternalLink,
      highlight: !isSafari
    },
    {
      number: 2,
      title: 'Toque no botão Compartilhar',
      description: 'Na parte inferior da tela, toque no ícone de compartilhar (quadrado com seta para cima)',
      icon: Share,
      visual: (
        <div className="flex justify-center my-3">
          <div className="bg-slate-700 rounded-xl p-4 inline-flex items-center gap-2">
            <div className="w-10 h-10 bg-cyan-500 rounded-lg flex items-center justify-center">
              <Share className="w-6 h-6 text-white" />
            </div>
            <span className="text-white text-sm">← Toque aqui</span>
          </div>
        </div>
      )
    },
    {
      number: 3,
      title: 'Adicionar à Tela de Início',
      description: 'Role a lista para baixo e toque em "Adicionar à Tela de Início"',
      icon: Plus,
      visual: (
        <div className="flex justify-center my-3">
          <div className="bg-slate-700 rounded-xl p-3 w-64">
            <div className="flex items-center gap-3 p-2 bg-slate-600 rounded-lg">
              <div className="w-8 h-8 bg-slate-500 rounded flex items-center justify-center">
                <Plus className="w-5 h-5 text-white" />
              </div>
              <span className="text-white text-sm">Adicionar à Tela de Início</span>
            </div>
          </div>
        </div>
      )
    },
    {
      number: 4,
      title: 'Confirme a instalação',
      description: 'Toque em "Adicionar" no canto superior direito',
      icon: CheckCircle2,
      visual: (
        <div className="flex justify-center my-3">
          <div className="bg-slate-700 rounded-xl p-3 w-64">
            <div className="flex items-center justify-between mb-3">
              <span className="text-slate-400 text-sm">Cancelar</span>
              <span className="text-cyan-400 font-semibold text-sm">Adicionar</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-cyan-500 rounded-xl flex items-center justify-center">
                <span className="text-white font-bold">G2</span>
              </div>
              <span className="text-white">G2eGente</span>
            </div>
          </div>
        </div>
      )
    },
    {
      number: 5,
      title: 'Pronto! Abra o app',
      description: 'Feche o Safari e procure o ícone "G2eGente" na sua tela inicial',
      icon: Check,
      visual: (
        <div className="flex justify-center my-3">
          <div className="bg-emerald-500/20 border border-emerald-500/50 rounded-xl p-4 inline-flex items-center gap-3">
            <div className="w-14 h-14 bg-cyan-500 rounded-2xl flex items-center justify-center shadow-lg">
              <span className="text-white font-bold text-lg">G2</span>
            </div>
            <div className="text-left">
              <p className="text-emerald-400 font-semibold">App instalado!</p>
              <p className="text-emerald-300 text-xs">Toque no ícone para abrir</p>
            </div>
          </div>
        </div>
      )
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-cyan-900">
      {/* Header */}
      <header className="p-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-10 h-10 bg-white/10 backdrop-blur rounded-lg flex items-center justify-center">
            <span className="text-cyan-400 font-bold text-lg">G2</span>
          </div>
          <span className="font-bold text-xl text-white">G2eGente</span>
        </div>
      </header>

      {/* Main Content */}
      <main className="px-4 pb-8">
        {/* Hero */}
        <div className="text-center py-8">
          <div className={`w-24 h-24 rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-lg ${
            showInstallSuccess 
              ? 'bg-gradient-to-br from-emerald-500 to-emerald-600 shadow-emerald-500/30' 
              : 'bg-gradient-to-br from-cyan-500 to-cyan-600 shadow-cyan-500/30'
          }`}>
            {showInstallSuccess ? (
              <Check className="w-12 h-12 text-white" />
            ) : (
              <Smartphone className="w-12 h-12 text-white" />
            )}
          </div>
          
          <h1 className="text-3xl font-bold text-white mb-3">
            {showInstallSuccess ? 'App Instalado!' : 'Baixe o App G2eGente'}
          </h1>
          <p className="text-slate-300 mb-6 max-w-sm mx-auto">
            {showInstallSuccess 
              ? 'Agora feche esta página e abra o app pelo ícone na tela inicial do seu celular.'
              : 'Instale nosso app gratuitamente e tenha acesso às melhores oportunidades de emprego.'
            }
          </p>

          {/* Install Button for Android/Desktop */}
          {!showInstallSuccess && !isIOS && (
            <Button 
              size="lg"
              onClick={handleInstallClick}
              className="w-full max-w-xs mx-auto text-lg py-6 rounded-xl shadow-lg bg-cyan-500 hover:bg-cyan-400 text-white"
              data-testid="install-app-btn"
            >
              <Download className="w-6 h-6 mr-2" />
              Instalar App Grátis
            </Button>
          )}

          {showInstallSuccess && (
            <div className="bg-emerald-500/20 border border-emerald-500/30 rounded-xl p-4 max-w-xs mx-auto">
              <p className="text-emerald-300 text-sm font-medium">
                ✓ Procure o ícone "G2eGente" na sua tela inicial e toque para abrir!
              </p>
            </div>
          )}

          {!showInstallSuccess && !isIOS && (
            <p className="text-slate-400 text-sm mt-3">
              100% grátis • Sem ocupar espaço • Funciona offline
            </p>
          )}
        </div>

        {/* iPhone Special Section */}
        {isIOS && !showInstallSuccess && (
          <div className="mb-8">
            {/* Safari Warning */}
            {!isSafari && (
              <div className="bg-amber-500/20 border border-amber-500/50 rounded-xl p-4 mb-6">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-6 h-6 text-amber-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="text-amber-300 font-semibold mb-1">Abra no Safari</h3>
                    <p className="text-amber-200/80 text-sm mb-3">
                      Para instalar o app no iPhone, você precisa usar o Safari. Copie o link abaixo e abra no Safari:
                    </p>
                    <div className="flex gap-2">
                      <input 
                        type="text" 
                        value="www.g2egente.com.br/app"
                        readOnly
                        className="flex-1 bg-amber-900/30 border border-amber-500/30 rounded-lg px-3 py-2 text-amber-100 text-sm"
                      />
                      <Button
                        size="sm"
                        onClick={() => {
                          navigator.clipboard.writeText('https://www.g2egente.com.br/app');
                          toast.success('Link copiado! Agora abra no Safari e cole na barra de endereço.');
                        }}
                        className="bg-amber-500 hover:bg-amber-400 text-white"
                      >
                        Copiar
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* iPhone Step-by-Step Instructions */}
            <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden backdrop-blur">
              <button 
                onClick={() => setShowInstructions(!showInstructions)}
                className="w-full p-4 flex items-center justify-between text-white"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-slate-700 rounded-full flex items-center justify-center">
                    <Smartphone className="w-5 h-5 text-cyan-400" />
                  </div>
                  <div className="text-left">
                    <span className="font-semibold block">Como instalar no iPhone</span>
                    <span className="text-slate-400 text-sm">Passo a passo simples</span>
                  </div>
                </div>
                <ChevronDown className={`w-5 h-5 transition-transform ${showInstructions ? 'rotate-180' : ''}`} />
              </button>
              
              {showInstructions && (
                <div className="border-t border-white/10">
                  {/* Progress Indicator */}
                  <div className="flex justify-center gap-2 p-4 bg-slate-800/50">
                    {iphoneSteps.map((step, i) => (
                      <button
                        key={i}
                        onClick={() => setCurrentStep(i)}
                        className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold transition-all ${
                          currentStep === i 
                            ? 'bg-cyan-500 text-white scale-110' 
                            : currentStep > i
                              ? 'bg-emerald-500 text-white'
                              : 'bg-slate-700 text-slate-400'
                        }`}
                      >
                        {currentStep > i ? <Check className="w-4 h-4" /> : step.number}
                      </button>
                    ))}
                  </div>

                  {/* Current Step Content */}
                  <div className="p-6">
                    {iphoneSteps.map((step, i) => (
                      currentStep === i && (
                        <div key={i} className="animate-in fade-in duration-300">
                          <div className="flex items-center gap-3 mb-4">
                            <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                              step.highlight ? 'bg-amber-500' : 'bg-cyan-500'
                            }`}>
                              <step.icon className="w-6 h-6 text-white" />
                            </div>
                            <div>
                              <p className="text-slate-400 text-xs uppercase tracking-wide">Passo {step.number}</p>
                              <h3 className="text-white font-semibold text-lg">{step.title}</h3>
                            </div>
                          </div>
                          
                          <p className="text-slate-300 mb-4">{step.description}</p>
                          
                          {step.visual}
                          
                          {/* Navigation */}
                          <div className="flex gap-3 mt-6">
                            {currentStep > 0 && (
                              <Button
                                variant="outline"
                                onClick={() => setCurrentStep(currentStep - 1)}
                                className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-700"
                              >
                                Voltar
                              </Button>
                            )}
                            {currentStep < iphoneSteps.length - 1 ? (
                              <Button
                                onClick={() => setCurrentStep(currentStep + 1)}
                                className="flex-1 bg-cyan-500 hover:bg-cyan-400 text-white"
                              >
                                Próximo Passo
                              </Button>
                            ) : (
                              <Button
                                onClick={() => toast.success('Agora feche o Safari e abra o app pelo ícone na tela inicial!')}
                                className="flex-1 bg-emerald-500 hover:bg-emerald-400 text-white"
                              >
                                <Check className="w-5 h-5 mr-2" />
                                Entendi!
                              </Button>
                            )}
                          </div>
                        </div>
                      )
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Features Grid */}
        <div className="grid grid-cols-2 gap-3 mb-8">
          {features.map((feature, i) => (
            <Card key={i} className="bg-white/5 border-white/10 backdrop-blur">
              <CardContent className="p-4 text-center">
                <feature.icon className="w-8 h-8 text-cyan-400 mx-auto mb-2" />
                <h3 className="font-semibold text-white text-sm">{feature.title}</h3>
                <p className="text-slate-400 text-xs">{feature.desc}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Android Manual Instructions */}
        {isAndroid && !showInstallSuccess && !canBeInstalled && (
          <div className="bg-white/5 border border-white/10 rounded-2xl p-4 backdrop-blur">
            <button 
              onClick={() => setShowInstructions(!showInstructions)}
              className="w-full flex items-center justify-between text-white"
            >
              <span className="font-semibold">Como instalar manualmente</span>
              <ChevronDown className={`w-5 h-5 transition-transform ${showInstructions ? 'rotate-180' : ''}`} />
            </button>
            
            {showInstructions && (
              <div className="mt-4 space-y-3">
                <ol className="text-slate-300 text-sm space-y-2">
                  <li className="flex items-start gap-2">
                    <span className="bg-cyan-500 text-white w-5 h-5 rounded-full flex items-center justify-center text-xs flex-shrink-0">1</span>
                    <span>Toque no <strong>menu (⋮)</strong> do navegador</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="bg-cyan-500 text-white w-5 h-5 rounded-full flex items-center justify-center text-xs flex-shrink-0">2</span>
                    <span>Selecione <strong>"Adicionar à tela inicial"</strong> ou <strong>"Instalar app"</strong></span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="bg-cyan-500 text-white w-5 h-5 rounded-full flex items-center justify-center text-xs flex-shrink-0">3</span>
                    <span>Confirme tocando em <strong>"Instalar"</strong></span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="bg-emerald-500 text-white w-5 h-5 rounded-full flex items-center justify-center text-xs flex-shrink-0">4</span>
                    <span><strong>Feche o navegador</strong> e abra o app pelo ícone na tela inicial</span>
                  </li>
                </ol>
              </div>
            )}
          </div>
        )}

        {/* Desktop Fallback */}
        {!isIOS && !isAndroid && !showInstallSuccess && (
          <div className="text-center text-slate-400 text-sm">
            <p>Para a melhor experiência, acesse pelo celular</p>
          </div>
        )}
      </main>
    </div>
  );
}
